/**
 * Supplier evaluation, performance events, scorecards, risk and the supplier reports
 * (FSD 4.2.2, P45 PS5-U65) — the feature's own wrappers over the generated client.
 *
 * Every write takes the caller's held `idempotencyKey` (X1 contract: one key per
 * submission, held by the screen with `useSubmissionKey`); nothing here mints one.
 *
 * Two risk endpoints serve the risk report (#126): `GET /reports/supplier-risk` is the
 * per-supplier exposure page (documents, screening, CAPAs) and
 * `GET /reports/supplier-risk-dashboard` is the rating distribution, the high-risk list
 * and the concentration dependencies. Both are read here; the risk report shows both.
 */

import {
  procurementEvaluationsGet,
  procurementEvaluationsList,
  procurementEvaluationsPackStatus,
  procurementEvaluationsRecordOutcome,
  procurementEvaluationsRequestPack,
  procurementEvaluationsRun,
  procurementScorecardWeightsGet,
  procurementScorecardWeightsSet,
  procurementSuppliersConcentrationReport,
  procurementSuppliersDocumentExpiryReport,
  procurementSuppliersEvents,
  procurementSuppliersOnboardingCycleReport,
  procurementSuppliersRecordEvent,
  procurementSuppliersRisk,
  procurementSuppliersRiskDashboard,
  procurementSuppliersScorecard,
  procurementSuppliersSetRiskProfile,
  type PageProcurementSupplierEvaluationDTO,
  type PageProcurementSupplierEventDTO,
  type ProcurementScorecardWeightsDTO,
  type ProcurementScorecardWeightsRequest,
  type ProcurementSupplierConcentrationQuery,
  type ProcurementSupplierConcentrationRowDTO,
  type ProcurementSupplierDocumentExpiryQuery,
  type ProcurementSupplierDocumentExpiryRowDTO,
  type ProcurementSupplierEvaluationDTO,
  type ProcurementSupplierEvaluationListQuery,
  type ProcurementSupplierEvaluationOutcomeRequest,
  type ProcurementSupplierEvaluationRequest,
  type ProcurementSupplierEventDTO,
  type ProcurementSupplierEventListQuery,
  type ProcurementSupplierEventRequest,
  type ProcurementSupplierOnboardingCycleQuery,
  type ProcurementSupplierOnboardingCycleRowDTO,
  type ProcurementSupplierRiskDashboardDTO,
  type ProcurementSupplierRiskDTO,
  type ProcurementSupplierRiskProfileRequest,
  type ProcurementSupplierScorecardDTO,
  type ProcurementSupplierScorecardQuery,
  type RenderStatusDTO,
} from '@erpx/api'

import { api } from '@/api/client'

/** Permission codes the evaluation screens check (mirrors `internal/procurement`). */
export const EVALUATION_PERMISSIONS = {
  /** Read scorecards and evaluation runs. */
  scorecardRead: 'procurement.scorecard.read',
  /** Run an evaluation, record an outcome, request the pack, set a risk profile. */
  supplierEvaluate: 'procurement.supplier.evaluate',
  /** Record a delivery / quality / service / invoice-accuracy event. */
  supplierEvent: 'procurement.supplier.event',
  /** Read a supplier's events and risk. */
  supplierRead: 'procurement.supplier.read',
  /** A downgrade outcome sets the preferred standing: the server re-checks this. */
  supplierPreferred: 'procurement.supplier.preferred',
  /** An exit outcome raises the supplier-exit request: the server re-checks this. */
  supplierManage: 'procurement.supplier.manage',
  /** Read the composite weights. */
  settingsRead: 'procurement.settings.read',
  /** Set the composite weights. */
  settingsManage: 'procurement.settings.manage',
  /** The supplier reports. */
  reportRead: 'procurement.report.read',
} as const

export const EVALUATION_KEYS = {
  all: ['procurement', 'evaluation'] as const,
  runs: ['procurement', 'evaluation', 'runs'] as const,
  runsPage: (params: Record<string, unknown>) =>
    ['procurement', 'evaluation', 'runs', 'list', params] as const,
  run: (id: number) => ['procurement', 'evaluation', 'runs', 'detail', id] as const,
  pack: (id: number) => ['procurement', 'evaluation', 'runs', 'pack', id] as const,
  weights: (companyId: number) => ['procurement', 'evaluation', 'weights', companyId] as const,
  supplier: (vendorId: number) => ['procurement', 'evaluation', 'supplier', vendorId] as const,
  scorecard: (vendorId: number, params: Record<string, unknown>) =>
    ['procurement', 'evaluation', 'supplier', vendorId, 'scorecard', params] as const,
  events: (vendorId: number, params: Record<string, unknown>) =>
    ['procurement', 'evaluation', 'supplier', vendorId, 'events', params] as const,
  risk: (vendorId: number) => ['procurement', 'evaluation', 'supplier', vendorId, 'risk'] as const,
  reports: ['procurement', 'evaluation', 'reports'] as const,
  documentExpiry: (params: Record<string, unknown>) =>
    ['procurement', 'evaluation', 'reports', 'document-expiry', params] as const,
  riskDashboard: (params: Record<string, unknown>) =>
    ['procurement', 'evaluation', 'reports', 'risk-dashboard', params] as const,
  onboardingCycle: (params: Record<string, unknown>) =>
    ['procurement', 'evaluation', 'reports', 'onboarding-cycle', params] as const,
  concentration: (params: Record<string, unknown>) =>
    ['procurement', 'evaluation', 'reports', 'concentration', params] as const,
} as const

export const EVENT_TYPES = ['delivery', 'quality', 'service', 'invoice_accuracy'] as const
export const SEVERITIES = ['low', 'medium', 'high', 'critical'] as const
export const OUTCOMES = ['confirm', 'downgrade', 'exit'] as const
export const FINANCIAL_SIGNALS = ['healthy', 'watch', 'distressed', 'unknown'] as const
export const GEOGRAPHY_RISKS = ['low', 'medium', 'high'] as const

export type EvaluationOutcome = (typeof OUTCOMES)[number]

/** Drop undefined/null/'' so a query string carries only what the user set. */
function compact<Q extends object>(query: Q): Q {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    out[key] = value
  }
  return out as Q
}

/* --------------------------------------------------------------- evaluations */

export function listEvaluations(
  query: ProcurementSupplierEvaluationListQuery,
): Promise<PageProcurementSupplierEvaluationDTO> {
  return procurementEvaluationsList(api(), compact(query))
}

export function runEvaluation(
  body: ProcurementSupplierEvaluationRequest,
  idempotencyKey: string,
): Promise<ProcurementSupplierEvaluationDTO> {
  return procurementEvaluationsRun(api(), body, idempotencyKey)
}

export function getEvaluation(id: number): Promise<ProcurementSupplierEvaluationDTO> {
  return procurementEvaluationsGet(api(), { id: String(id) })
}

export function recordOutcome(
  id: number,
  body: ProcurementSupplierEvaluationOutcomeRequest,
  idempotencyKey: string,
): Promise<ProcurementSupplierEvaluationDTO> {
  return procurementEvaluationsRecordOutcome(api(), { id: String(id) }, body, idempotencyKey)
}

/** Ask for the review pack at the run's current version (once per version, server-side). */
export function requestPack(id: number): Promise<RenderStatusDTO> {
  return procurementEvaluationsRequestPack(api(), { id: String(id) })
}

export function packStatus(id: number): Promise<RenderStatusDTO> {
  return procurementEvaluationsPackStatus(api(), { id: String(id) })
}

/* ------------------------------------------------------------------- weights */

export function getWeights(companyId: number): Promise<ProcurementScorecardWeightsDTO> {
  return procurementScorecardWeightsGet(api(), { companyId: String(companyId) })
}

export function setWeights(
  companyId: number,
  body: ProcurementScorecardWeightsRequest,
  idempotencyKey: string,
): Promise<ProcurementScorecardWeightsDTO> {
  return procurementScorecardWeightsSet(api(), { companyId: String(companyId) }, body, idempotencyKey)
}

/* ------------------------------------------------- supplier events and scores */

export function recordEvent(
  vendorId: number,
  body: ProcurementSupplierEventRequest,
  idempotencyKey: string,
): Promise<ProcurementSupplierEventDTO> {
  return procurementSuppliersRecordEvent(api(), { vendorId: String(vendorId) }, body, idempotencyKey)
}

export function listEvents(
  vendorId: number,
  query: ProcurementSupplierEventListQuery,
): Promise<PageProcurementSupplierEventDTO> {
  return procurementSuppliersEvents(api(), { vendorId: String(vendorId) }, compact(query))
}

export function getScorecard(
  vendorId: number,
  query: ProcurementSupplierScorecardQuery,
): Promise<ProcurementSupplierScorecardDTO> {
  return procurementSuppliersScorecard(api(), { vendorId: String(vendorId) }, compact(query))
}

export function getRisk(vendorId: number): Promise<ProcurementSupplierRiskDTO> {
  return procurementSuppliersRisk(api(), { vendorId: String(vendorId) })
}

export function setRiskProfile(
  vendorId: number,
  body: ProcurementSupplierRiskProfileRequest,
  idempotencyKey: string,
): Promise<ProcurementSupplierRiskDTO> {
  return procurementSuppliersSetRiskProfile(api(), { vendorId: String(vendorId) }, body, idempotencyKey)
}

/* ------------------------------------------------------------------- reports */

export function documentExpiryReport(
  query: ProcurementSupplierDocumentExpiryQuery,
): Promise<ProcurementSupplierDocumentExpiryRowDTO[]> {
  return procurementSuppliersDocumentExpiryReport(api(), compact(query))
}

export function riskDashboard(
  query: { companyId?: number | null },
): Promise<ProcurementSupplierRiskDashboardDTO> {
  return procurementSuppliersRiskDashboard(
    api(),
    query.companyId === null || query.companyId === undefined ? {} : { companyId: query.companyId },
  )
}

export function onboardingCycleReport(
  query: ProcurementSupplierOnboardingCycleQuery,
): Promise<ProcurementSupplierOnboardingCycleRowDTO[]> {
  return procurementSuppliersOnboardingCycleReport(api(), compact(query))
}

export function concentrationReport(
  query: ProcurementSupplierConcentrationQuery,
): Promise<ProcurementSupplierConcentrationRowDTO[]> {
  return procurementSuppliersConcentrationReport(api(), compact(query))
}

/* ------------------------------------------------------------------- helpers */

/** The badge tone for a risk rating. */
export function riskTone(rating: string | null | undefined): 'neutral' | 'success' | 'warning' | 'danger' {
  switch (rating) {
    case 'low':
      return 'success'
    case 'medium':
      return 'warning'
    case 'high':
    case 'critical':
      return 'danger'
    default:
      return 'neutral'
  }
}

/** The badge tone for a recommended action or an outcome. */
export function actionTone(action: string | null | undefined): 'neutral' | 'success' | 'warning' | 'danger' {
  switch (action) {
    case 'confirm':
      return 'success'
    case 'downgrade':
      return 'warning'
    case 'exit':
      return 'danger'
    default:
      return 'neutral'
  }
}

/** Pack render states that are still moving and worth polling. */
export function packInFlight(status: string | null | undefined): boolean {
  return status === 'pending' || status === 'rendering'
}
